import { Core } from "@faire/design-tokens";
import { trackNavigationTreeUxCategoriesMenuButtonClick } from "@faire/web-api/events/navigationTreeUx/click/categoriesMenuButton";
import { trackNavigationTreeUxCategoriesMenuButtonView } from "@faire/web-api/events/navigationTreeUx/view/categoriesMenuButton";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { Close } from "@faire/web/slate/icons/Close";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import ClickOutside from "@faire/web/ui/ClickOutside";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styled, { css } from "styled-components";

import { LazyHeaderCategoriesDropdown } from "@faire/retailer/components/Header/desktop/HeaderCategoriesDropdown/LazyCategoriesDropdown";
import { HeaderDropdown } from "@faire/retailer/components/Header/styled";
import { useIsHeaderDropdownVisible } from "@faire/retailer/components/HeaderPriorityRenderer/useIsHeaderDropdownVisible";
import { Chevron } from "@faire/retailer/icons/Chevron/Chevron";

export const HeaderCategoriesButton: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();

  const [shouldShowDropdown, setShouldShowDropdown] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const { setHeaderDropdownVisibility } = useIsHeaderDropdownVisible();

  const toggleHandler = useCallback(
    (shouldShowDropdown: boolean, isUserClick: boolean) => {
      setShouldShowDropdown(shouldShowDropdown);
      setHeaderDropdownVisibility("CATEGORIES", shouldShowDropdown);

      if (isUserClick) {
        trackNavigationTreeUxCategoriesMenuButtonClick(
          shouldShowDropdown ? "OPEN" : "CLOSE"
        );
      }
    },
    [setHeaderDropdownVisibility]
  );

  useEffect(() => {
    trackNavigationTreeUxCategoriesMenuButtonView();
  }, []);

  const closeDropdown = useCallback(() => {
    toggleHandler(false, false);
    buttonRef.current?.blur();
  }, [toggleHandler]);

  return (
    <ClickOutside onClickOutside={closeDropdown}>
      <DropdownButton
        buttonRef={buttonRef}
        onClick={() => toggleHandler(!shouldShowDropdown, true)}
        aria-label={strictLocalize({
          defaultMessage: "All categories",
          description: { text: "A button that opens a menu of all categories" },
        })}
        aria-haspopup="menu"
        variant="tertiary"
        $isSelected={shouldShowDropdown}
      >
        <StrictLocalMsg
          defaultMessage="All categories"
          description={{
            text: "A button that opens a menu of all categories",
          }}
        />
        <Spacer size="1x" />
        <IconWrapper>
          {shouldShowDropdown ? <Close /> : <Chevron direction="down" />}
        </IconWrapper>
      </DropdownButton>
      {shouldShowDropdown ? (
        <HeaderDropdown $right="0px" $width="100vw" $noBoxShadow>
          <Suspense fallback={null}>
            <LazyHeaderCategoriesDropdown handleCloseMenu={closeDropdown} />
          </Suspense>
        </HeaderDropdown>
      ) : null}
    </ClickOutside>
  );
};

const DropdownButton = styled(Button)<{ $isSelected?: boolean }>`
  padding: ${getSpacing(10, "2x")};
  text-decoration: none;
  border-radius: ${getSpacing("4x")};

  ${(props) =>
    props.$isSelected
      ? css`
          background-color: ${Core.surface.subdued};
          text-decoration: none;
        `
      : ""}

  &:hover,
  &:focus {
    background-color: ${Core.surface.subdued};
    text-decoration: none;
  }
`;

const IconWrapper = styled.div`
  svg {
    width: 8px;
    height: 8px;
    stroke-width: 1.5;
  }
`;
